import { useEffect } from 'react'
import { ConnectComponentsProvider } from '@stripe/react-connect-js'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import StripeConnectEmbeddedSurfaceSkeleton from './StripeConnectEmbeddedSurfaceSkeleton'
import StripeConnectPaintedSurface from './StripeConnectPaintedSurface'
import StripeConnectSection from './StripeConnectSection'
import {
  STRIPE_CONNECT_GENERIC_MAX_HYDRATION_MS,
  useStripeConnectManager,
} from './useStripeConnectManager'
import { AlertTriangle, RefreshCw } from 'lucide-react'

export default function StripeConnectEmbeddedComponentManager({
  component: EmbeddedComponent,
  description,
  errorTitle = 'Stripe tools are unavailable right now',
  minHeightClassName,
  organizationId,
  accountContext = 'current',
  onReadyChange,
  onSessionConflict,
  showSectionHeader = true,
  surface,
  toastTitle,
  title,
}) {
  const {
    connectInstance,
    isEmbeddedSurfaceHydrating,
    isLoading,
    loadErrorMessage,
    handleLoadError,
    handleLoaderStart,
    handleSurfacePainted,
    renderAttemptKey,
    retryLoad,
  } = useStripeConnectManager({
    organizationId,
    accountContext,
    onSessionConflict,
    maxHydrationMs: STRIPE_CONNECT_GENERIC_MAX_HYDRATION_MS,
    surface,
    toastTitle: toastTitle ?? `Unable to load Stripe ${title.toLowerCase()}`,
  })
  const showPlaceholder = isLoading || isEmbeddedSurfaceHydrating
  const isRenderable = Boolean(loadErrorMessage) || (Boolean(connectInstance) && !showPlaceholder)

  useEffect(() => {
    onReadyChange?.(isRenderable)
  }, [isRenderable, onReadyChange])

  if (loadErrorMessage) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>{errorTitle}</AlertTitle>
        <AlertDescription>
          <p>{loadErrorMessage}</p>
          <Button variant="outline" size="sm" className="mt-2" onClick={retryLoad}>
            <RefreshCw className="size-3.5" aria-hidden="true" />
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <StripeConnectSection title={title} description={description} showHeader={showSectionHeader}>
      <StripeConnectPaintedSurface
        minHeightClassName={minHeightClassName}
        onLoaderStart={handleLoaderStart}
        onSurfacePainted={handleSurfacePainted}
        placeholder={<StripeConnectEmbeddedSurfaceSkeleton rows={5} showSummary />}
        renderAttemptKey={renderAttemptKey}
        showPlaceholder={showPlaceholder}
      >
        {connectInstance
          ? ({ handleLoaderStart: handlePaintAwareLoaderStart }) => (
              <ConnectComponentsProvider connectInstance={connectInstance}>
                <EmbeddedComponent
                  key={renderAttemptKey}
                  onLoadError={handleLoadError}
                  onLoaderStart={handlePaintAwareLoaderStart}
                />
              </ConnectComponentsProvider>
            )
          : null}
      </StripeConnectPaintedSurface>
    </StripeConnectSection>
  )
}
